/**
 * MM Defense System — Trigger Evaluation Engine
 *
 * Runs 11 trigger checks against a TriggerSnapshot and returns fired triggers
 * with tier1/tier2/tier3 severity escalation.
 *
 * Severity escalation pattern:
 *   tier1: informational — value crosses 50% of threshold
 *   tier2: threshold breached — the configured threshold is exceeded
 *   tier3: critical — 2x the tier2 value, or a dedicated critical config field
 *
 * Pure functions, no side effects. All state comes in via TriggerSnapshot.
 */

import type {
  TriggerSnapshot,
  TriggerConfig,
  TriggerFiring,
  TriggerSeverity,
  TriggerType,
} from './types';
import { DEFAULT_TRIGGER_CONFIG } from './types';

// ── Helpers ──

function firing(
  type: TriggerType,
  severity: TriggerSeverity,
  message: string,
  tickers: string[],
  value: number,
  threshold: number,
  evaluatedAtMs: number
): TriggerFiring {
  return { type, severity, message, tickers, value, threshold, evaluatedAtMs };
}

/**
 * Determine severity for a trigger with explicit critical config fields.
 * Returns the highest applicable severity, or null if below tier1.
 */
function severityWithCritical(
  value: number,
  tier2Threshold: number,
  tier3Threshold: number
): TriggerSeverity | null {
  if (value >= tier3Threshold) return 'tier3';
  if (value >= tier2Threshold) return 'tier2';
  if (value >= tier2Threshold * 0.5) return 'tier1';
  return null;
}

/**
 * Determine severity for a trigger using the 2x-tier2 pattern for tier3.
 * Returns the highest applicable severity, or null if below tier1.
 */
function severityDefault(value: number, tier2Threshold: number): TriggerSeverity | null {
  if (value >= tier2Threshold * 2) return 'tier3';
  if (value >= tier2Threshold) return 'tier2';
  if (value >= tier2Threshold * 0.5) return 'tier1';
  return null;
}

// ── Individual Trigger Checks ──

/**
 * 1. Unpaired Fill Timer
 * For each unpaired fill, check how long it has been unpaired.
 */
export function checkUnpairedFillTimer(
  snapshot: TriggerSnapshot,
  config: TriggerConfig
): TriggerFiring[] {
  const results: TriggerFiring[] = [];

  for (const unpaired of snapshot.unpairedFills) {
    const ageSec = unpaired.ageMs / 1000;
    const severity = severityWithCritical(
      ageSec,
      config.unpairedFillTimerSec,
      config.unpairedFillTimerCriticalSec
    );
    if (severity) {
      const ticker = unpaired.fill.ticker;
      results.push(
        firing(
          'unpaired_fill_timer',
          severity,
          `Unpaired fill on ${ticker} aged ${ageSec.toFixed(1)}s (threshold: ${config.unpairedFillTimerSec}s)`,
          [ticker],
          ageSec,
          config.unpairedFillTimerSec,
          snapshot.nowMs
        )
      );
    }
  }

  return results;
}

/**
 * 2. Post-Fill Drift
 * For each unpaired fill with non-null driftCents, check if adverse drift exceeds threshold.
 * driftCents < 0 means adverse.
 */
export function checkPostFillDrift(
  snapshot: TriggerSnapshot,
  config: TriggerConfig
): TriggerFiring[] {
  const results: TriggerFiring[] = [];

  for (const unpaired of snapshot.unpairedFills) {
    if (unpaired.driftCents === null) continue;
    // Only fire on adverse drift (negative)
    if (unpaired.driftCents >= 0) continue;

    const absDrift = Math.abs(unpaired.driftCents);
    const severity = severityWithCritical(
      absDrift,
      config.postFillDriftTicks,
      config.postFillDriftTicksCritical
    );
    if (severity) {
      const ticker = unpaired.fill.ticker;
      results.push(
        firing(
          'post_fill_drift',
          severity,
          `Adverse drift of ${absDrift} ticks on ${ticker} (threshold: ${config.postFillDriftTicks})`,
          [ticker],
          absDrift,
          config.postFillDriftTicks,
          snapshot.nowMs
        )
      );
    }
  }

  return results;
}

/**
 * 3. Fill Side Imbalance
 * Count recent fills by side (yes vs no). Fire if ratio of dominant side >= threshold.
 * Requires at least 4 fills.
 */
export function checkFillSideImbalance(
  snapshot: TriggerSnapshot,
  config: TriggerConfig
): TriggerFiring[] {
  const fills = snapshot.recentFills;
  if (fills.length < 4) return [];

  let yesCount = 0;
  let noCount = 0;
  for (const fill of fills) {
    if (fill.side === 'yes') yesCount++;
    else noCount++;
  }

  const total = yesCount + noCount;
  const ratio = Math.max(yesCount, noCount) / total;
  const dominantSide = yesCount >= noCount ? 'YES' : 'NO';

  const severity = severityDefault(ratio, config.fillSideImbalanceRatio);
  if (severity) {
    const tickers = [...new Set(fills.map((f) => f.ticker))];
    return [
      firing(
        'fill_side_imbalance',
        severity,
        `Fill side imbalance: ${(ratio * 100).toFixed(0)}% ${dominantSide} (threshold: ${(config.fillSideImbalanceRatio * 100).toFixed(0)}%)`,
        tickers,
        ratio,
        config.fillSideImbalanceRatio,
        snapshot.nowMs
      ),
    ];
  }

  return [];
}

/**
 * 4. Time-to-Fill Asymmetry
 * Group recent fills by side, compute average time between consecutive fills per side.
 * Fire if ratio of slow/fast side >= threshold. Requires at least 2 fills per side.
 */
export function checkTimeToFillAsymmetry(
  snapshot: TriggerSnapshot,
  config: TriggerConfig
): TriggerFiring[] {
  const fills = snapshot.recentFills;
  const yesFills = fills
    .filter((f) => f.side === 'yes')
    .sort((a, b) => a.timestampMs - b.timestampMs);
  const noFills = fills
    .filter((f) => f.side === 'no')
    .sort((a, b) => a.timestampMs - b.timestampMs);

  if (yesFills.length < 2 || noFills.length < 2) return [];

  const avgGap = (sorted: typeof fills): number => {
    let totalGap = 0;
    for (let i = 1; i < sorted.length; i++) {
      totalGap += sorted[i].timestampMs - sorted[i - 1].timestampMs;
    }
    return totalGap / (sorted.length - 1);
  };

  const yesAvg = avgGap(yesFills);
  const noAvg = avgGap(noFills);

  const minAvg = Math.min(yesAvg, noAvg);
  if (minAvg === 0) return [];

  const ratio = Math.max(yesAvg, noAvg) / minAvg;

  const severity = severityDefault(ratio, config.timeToFillAsymmetryRatio);
  if (severity) {
    const tickers = [...new Set(fills.map((f) => f.ticker))];
    return [
      firing(
        'time_to_fill_asymmetry',
        severity,
        `Fill timing asymmetry: ${ratio.toFixed(1)}x between sides (threshold: ${config.timeToFillAsymmetryRatio}x)`,
        tickers,
        ratio,
        config.timeToFillAsymmetryRatio,
        snapshot.nowMs
      ),
    ];
  }

  return [];
}

/**
 * 5. Book Pressure Skew
 * For each ticker in bookMetrics, check if abs(imbalance) >= threshold.
 */
export function checkBookPressureSkew(
  snapshot: TriggerSnapshot,
  config: TriggerConfig
): TriggerFiring[] {
  const results: TriggerFiring[] = [];

  for (const [ticker, metrics] of snapshot.bookMetrics) {
    const absImbalance = Math.abs(metrics.imbalance);
    const severity = severityDefault(absImbalance, config.bookPressureSkewThreshold);
    if (severity) {
      const direction = metrics.imbalance > 0 ? 'bid-heavy' : 'ask-heavy';
      results.push(
        firing(
          'book_pressure_skew',
          severity,
          `Book pressure skew on ${ticker}: ${absImbalance.toFixed(2)} ${direction} (threshold: ${config.bookPressureSkewThreshold})`,
          [ticker],
          absImbalance,
          config.bookPressureSkewThreshold,
          snapshot.nowMs
        )
      );
    }
  }

  return results;
}

/**
 * 6. Volume Spike
 * For each ticker, compare volume60s.total to volume15mAvg * volumeSpikeMultiple.
 */
export function checkVolumeSpike(
  snapshot: TriggerSnapshot,
  config: TriggerConfig
): TriggerFiring[] {
  const results: TriggerFiring[] = [];

  for (const [ticker, vol60s] of snapshot.volume60s) {
    const avg15m = snapshot.volume15mAvg.get(ticker);
    if (avg15m === undefined || avg15m === 0) continue;

    const ratio = vol60s.total / avg15m;
    const severity = severityDefault(ratio, config.volumeSpikeMultiple);
    if (severity) {
      results.push(
        firing(
          'volume_spike',
          severity,
          `Volume spike on ${ticker}: ${ratio.toFixed(1)}x normal (threshold: ${config.volumeSpikeMultiple}x)`,
          [ticker],
          ratio,
          config.volumeSpikeMultiple,
          snapshot.nowMs
        )
      );
    }
  }

  return results;
}

/**
 * 7. One-Sided Volume
 * For each ticker in volume60s, check if max(yesSide, noSide) / total >= threshold.
 * Requires total > 0.
 */
export function checkOneSidedVolume(
  snapshot: TriggerSnapshot,
  config: TriggerConfig
): TriggerFiring[] {
  const results: TriggerFiring[] = [];

  for (const [ticker, vol] of snapshot.volume60s) {
    if (vol.total <= 0) continue;

    const ratio = Math.max(vol.yesSide, vol.noSide) / vol.total;
    const dominantSide = vol.yesSide >= vol.noSide ? 'YES' : 'NO';
    const severity = severityDefault(ratio, config.oneSidedVolumeRatio);
    if (severity) {
      results.push(
        firing(
          'one_sided_volume',
          severity,
          `One-sided volume on ${ticker}: ${(ratio * 100).toFixed(0)}% ${dominantSide} (threshold: ${(config.oneSidedVolumeRatio * 100).toFixed(0)}%)`,
          [ticker],
          ratio,
          config.oneSidedVolumeRatio,
          snapshot.nowMs
        )
      );
    }
  }

  return results;
}

/**
 * 8. Spread Blowout
 * For each ticker in bookMetrics with non-null spreadCents, compare to normalSpread.
 */
export function checkSpreadBlowout(
  snapshot: TriggerSnapshot,
  config: TriggerConfig
): TriggerFiring[] {
  const results: TriggerFiring[] = [];

  for (const [ticker, metrics] of snapshot.bookMetrics) {
    if (metrics.spreadCents === null) continue;
    const normal = snapshot.normalSpread.get(ticker);
    if (normal === undefined || normal === 0) continue;

    const multiple = metrics.spreadCents / normal;
    const severity = severityDefault(multiple, config.spreadBlowoutMultiple);
    if (severity) {
      results.push(
        firing(
          'spread_blowout',
          severity,
          `Spread blowout on ${ticker}: ${metrics.spreadCents}c vs ${normal}c normal (${multiple.toFixed(1)}x, threshold: ${config.spreadBlowoutMultiple}x)`,
          [ticker],
          multiple,
          config.spreadBlowoutMultiple,
          snapshot.nowMs
        )
      );
    }
  }

  return results;
}

/**
 * 9. Correlated Market Move
 * For each ticker in correlatedPrices, check if abs(currentCents - previousCents) >= threshold.
 */
export function checkCorrelatedMarketMove(
  snapshot: TriggerSnapshot,
  config: TriggerConfig
): TriggerFiring[] {
  const results: TriggerFiring[] = [];

  for (const [ticker, prices] of snapshot.correlatedPrices) {
    const movement = Math.abs(prices.currentCents - prices.previousCents);
    const severity = severityDefault(movement, config.correlatedMarketMoveCents);
    if (severity) {
      const direction = prices.currentCents > prices.previousCents ? 'up' : 'down';
      results.push(
        firing(
          'correlated_market_move',
          severity,
          `Correlated market move on ${ticker}: ${movement}c ${direction} (threshold: ${config.correlatedMarketMoveCents}c)`,
          [ticker],
          movement,
          config.correlatedMarketMoveCents,
          snapshot.nowMs
        )
      );
    }
  }

  return results;
}

/**
 * 10. Stale Quote
 * For each ticker in lastUpdateMs, check how many seconds since last update.
 */
export function checkStaleQuote(snapshot: TriggerSnapshot, config: TriggerConfig): TriggerFiring[] {
  const results: TriggerFiring[] = [];

  for (const [ticker, lastMs] of snapshot.lastUpdateMs) {
    const staleSec = (snapshot.nowMs - lastMs) / 1000;
    const severity = severityWithCritical(
      staleSec,
      config.staleQuoteSec,
      config.staleQuoteCriticalSec
    );
    if (severity) {
      results.push(
        firing(
          'stale_quote',
          severity,
          `Stale quote on ${ticker}: ${staleSec.toFixed(1)}s since last update (threshold: ${config.staleQuoteSec}s)`,
          [ticker],
          staleSec,
          config.staleQuoteSec,
          snapshot.nowMs
        )
      );
    }
  }

  return results;
}

/**
 * 11. Exposure Breach
 * For each ticker in exposureByTicker, check if exposure >= threshold.
 */
export function checkExposureBreach(
  snapshot: TriggerSnapshot,
  config: TriggerConfig
): TriggerFiring[] {
  const results: TriggerFiring[] = [];

  for (const [ticker, exposure] of snapshot.exposureByTicker) {
    const severity = severityDefault(exposure, config.exposureBreachDollars);
    if (severity) {
      results.push(
        firing(
          'exposure_breach',
          severity,
          `Exposure breach on ${ticker}: $${exposure.toLocaleString()} (threshold: $${config.exposureBreachDollars.toLocaleString()})`,
          [ticker],
          exposure,
          config.exposureBreachDollars,
          snapshot.nowMs
        )
      );
    }
  }

  return results;
}

// ── Main Entry Point ──

/** All individual checker functions in evaluation order. */
const ALL_CHECKS: Array<(snapshot: TriggerSnapshot, config: TriggerConfig) => TriggerFiring[]> = [
  checkUnpairedFillTimer,
  checkPostFillDrift,
  checkFillSideImbalance,
  checkTimeToFillAsymmetry,
  checkBookPressureSkew,
  checkVolumeSpike,
  checkOneSidedVolume,
  checkSpreadBlowout,
  checkCorrelatedMarketMove,
  checkStaleQuote,
  checkExposureBreach,
];

/**
 * Evaluate all 11 triggers against a state snapshot.
 *
 * @param snapshot - Current system state
 * @param config - Optional partial config (merged with DEFAULT_TRIGGER_CONFIG)
 * @returns All fired triggers across all checks
 */
export function evaluateTriggers(
  snapshot: TriggerSnapshot,
  config?: Partial<TriggerConfig>
): TriggerFiring[] {
  const merged: TriggerConfig = { ...DEFAULT_TRIGGER_CONFIG, ...config };
  const results: TriggerFiring[] = [];

  for (const check of ALL_CHECKS) {
    const firings = check(snapshot, merged);
    results.push(...firings);
  }

  return results;
}
